import React, { useState, useEffect } from "react";

import { IconRefreshCw } from "@frontend/common";

import { useRegulatoryClassificationFilterQuery } from "@api/regulatory/regulatoryClassification/useRegulatoryClassification";
import BorderBox from "@atoms/BorderBox/BorderBox";
import FilterTag from "@atoms/FilterTag/FilterTag";
import IconCheck from "@images/common/icon-filter-check-white.svg";
import XMarkIcon from "@images/pressList/icon-x-mark-red.svg";
import { ClassificationList } from "@PressListContainer/classificationInfo.constants";
import { useRegulatoryListFilterState } from "@stores/regulatoryList";

import * as S from "./RegulatoryClassificationFilter.style";

interface Props {
  closeModal: () => void;
}

const RegulatoryClassificationFilter = ({ closeModal }: Props) => {
  const [regulatoryListFilter, setRegulatoryListFilter] =
    useRegulatoryListFilterState();

  const { classification } = regulatoryListFilter;

  const { data: classificationFilterData } =
    useRegulatoryClassificationFilterQuery();

  const [classificationList, setClassificationList] = useState<
    ClassificationList[]
  >(classificationFilterData?.ai || []);
  const [isAllChecked, setIsAllChecked] = useState(false);

  const changeClassificationTargetList = (
    event: React.ChangeEvent<HTMLInputElement>,
    list: ClassificationList[],
  ) => {
    return list.map((item) =>
      item.code === event.currentTarget.value
        ? { ...item, isCheck: !item.isCheck }
        : item,
    );
  };

  const handleClassificationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const targetElement = event.currentTarget;

    if (targetElement.value === "all") {
      setIsAllChecked(targetElement.checked);
      setClassificationList(
        classificationList.map((c) => ({
          ...c,
          isCheck: targetElement.checked,
        })),
      );
    } else {
      setClassificationList(
        changeClassificationTargetList(event, classificationList),
      );
    }
  };

  const handleConfirm = () => {
    const selectedClassification = classificationList
      .filter((c) => c.isCheck)
      .map((c) => c.code);

    setRegulatoryListFilter({
      ...regulatoryListFilter,
      classification: selectedClassification,
      page: 1,
    });
    closeModal();
  };

  const handleReset = () => {
    setIsAllChecked(false);
    setClassificationList(
      (classificationFilterData?.ai || []).map((c) => ({
        ...c,
        isCheck: false,
      })),
    );
  };

  useEffect(() => {
    setClassificationList(
      (classificationFilterData?.ai || []).map((c) => ({
        ...c,
        isCheck: classification.includes(c.code),
      })),
    );
  }, [classification, classificationFilterData?.ai]);

  useEffect(() => {
    const totalItems = classificationList.length;
    const checkedItems = classificationList.filter(
      (item) => item.isCheck,
    ).length;

    setIsAllChecked(totalItems > 0 && totalItems === checkedItems);
  }, [classificationList]);

  const totalCheckedClassification = classificationList.filter(
    (item) => item.isCheck,
  ).length;

  const confirmButtonText =
    totalCheckedClassification > 1
      ? "Apply Classification Filters"
      : "Apply Classification Filter";

  return (
    <S.StatusFilterWrapper>
      <BorderBox padding="16">
        <S.StatusFilterContentWrapper>
          <S.StatusFilterTitleRow>
            <S.FilterResetButton
              onClick={handleReset}
              disabled={totalCheckedClassification < 1}
            >
              {totalCheckedClassification < 1 ? (
                <IconRefreshCw width={20} height={20} color="#a5a5a5" />
              ) : (
                <IconRefreshCw width={20} height={20} color="#515C6C" />
              )}
              <S.FilterResetButtonText>Reset</S.FilterResetButtonText>
            </S.FilterResetButton>
            <S.ClassificationCloseButton type="button" onClick={closeModal}>
              <S.ClassificationXMark>
                <img src={XMarkIcon} alt="close filter" />
              </S.ClassificationXMark>
              <S.ClassificationCloseText>Close</S.ClassificationCloseText>
            </S.ClassificationCloseButton>
          </S.StatusFilterTitleRow>
          <S.StatusFilterTitleWrapper>
            <S.StatusFilterTitle>Classification</S.StatusFilterTitle>
            <FilterTag
              name="classification"
              value="all"
              onChange={handleClassificationChange}
              checked={isAllChecked}
              isAll
            >
              Select All
            </FilterTag>
          </S.StatusFilterTitleWrapper>
          <S.StatusFilterRow>
            <S.StatusFilterFieldset>
              {classificationList.map((item) => (
                <FilterTag
                  key={`classification-${item.code}`}
                  tagType="checkbox"
                  onChange={handleClassificationChange}
                  name="classification"
                  value={item.code}
                  checked={item.isCheck}
                  filterType="regulatoryClassification"
                  topicSelected
                >
                  {item.name}
                </FilterTag>
              ))}
            </S.StatusFilterFieldset>
          </S.StatusFilterRow>
          <S.ConfirmButtonWrapper>
            <S.ConfirmButton onClick={handleConfirm}>
              <img src={IconCheck} alt="filter check icon" />
              <S.ConfirmButtonText>{confirmButtonText}</S.ConfirmButtonText>
            </S.ConfirmButton>
          </S.ConfirmButtonWrapper>
        </S.StatusFilterContentWrapper>
      </BorderBox>
    </S.StatusFilterWrapper>
  );
};

export default RegulatoryClassificationFilter;
